import { asImageSrc, isFilled } from '@prismicio/client';

import type { Content } from '@prismicio/client';

/** Text direction of a language. Drives `dir` on the document element. */
export type Direction = 'ltr' | 'rtl';

/** One tile in the language picker. */
export type LanguageOption = {
  /** Lower-case language code; the `language` document's UID. */
  code: string;
  /** The language's name in itself, e.g. `中文`. */
  nativeName: string;
  /**
   * The ISO 639-1 code the picker shows in place of a flag, e.g. `ZH`. SLSA
   * asked for codes rather than flags (UAT 7.1, 7.2). From the `language`
   * document's Code shown field, else the upper-cased UID.
   */
  codeLabel: string;
  /** Optional artwork from the `language` document's Flag field. When set it replaces the code. */
  flagUrl: string | null;
  direction: Direction;
};

export const ENGLISH_CODE = 'en';

/**
 * Cookie the client writes when the learner picks a language, so the server can
 * render the right copy on the first paint. The root loader reads it; the store
 * writes it through `writeLanguageCookie`.
 */
export const LANGUAGE_COOKIE = 'bp-language';

/**
 * The picker entry used when the repository has no `language` documents — which
 * is the case in production today. English is always offered so the app is
 * usable before any content exists.
 */
export const englishOption: LanguageOption = {
  code: ENGLISH_CODE,
  nativeName: 'English',
  codeLabel: 'EN',
  flagUrl: null,
  direction: 'ltr',
};

/**
 * The seven languages the live app offers, with the same native names. No
 * flags: SLSA asked for ISO 639-1 codes in their place, so a flag only shows
 * when an editor sets one on the `language` document.
 * Served whenever the repository has no `language` documents, so the picker
 * matches production before content staff have authored a single one. A
 * document of the same UID always wins.
 */
export const bundledLanguages: Array<LanguageOption> = [
  englishOption,
  { code: 'zh', nativeName: '普通话', codeLabel: 'ZH', flagUrl: null, direction: 'ltr' },
  { code: 'ja', nativeName: 'にほんご', codeLabel: 'JA', flagUrl: null, direction: 'ltr' },
  { code: 'pa', nativeName: 'ਪੰਜਾਬੀ', codeLabel: 'PA', flagUrl: null, direction: 'ltr' },
  { code: 'hi', nativeName: 'हिन्दी', codeLabel: 'HI', flagUrl: null, direction: 'ltr' },
  { code: 'ar', nativeName: 'العربية', codeLabel: 'AR', flagUrl: null, direction: 'rtl' },
  { code: 'de', nativeName: 'Deutsch', codeLabel: 'DE', flagUrl: null, direction: 'ltr' },
];

/** The bundled entry for a code, when there is one. */
export function bundledLanguage(code: string): LanguageOption | undefined {
  return bundledLanguages.find((option) => option.code === code);
}

/**
 * Writes the language cookie so the *next* server render picks the same
 * language as the store — `<html lang dir>` and every string come from the root
 * loader, which only sees this cookie. Client-only; a no-op on the server.
 */
export function writeLanguageCookie(code: string): void {
  if (typeof document === 'undefined') {
    return;
  }

  // A year, site-wide, lax: the same defaults the old app's Dexie row had by
  // living in the browser indefinitely.
  document.cookie = `${LANGUAGE_COOKIE}=${encodeURIComponent(code)}; path=/; max-age=31536000; samesite=lax`;
}

/**
 * Forgets the language, so the next server render falls back to English.
 * Starting over has to clear this as well as the store: the root loader only
 * sees the cookie, so a learner who erased everything came back to a welcome
 * screen still laid out right-to-left in Arabic.
 */
export function clearLanguageCookie(): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.cookie = `${LANGUAGE_COOKIE}=; path=/; max-age=0; samesite=lax`;
}

export function toLanguageOption(doc: Content.LanguageDocument): LanguageOption {
  const nativeName = doc.data.native_name?.trim();

  return {
    code: doc.uid,
    nativeName: nativeName && nativeName !== '' ? nativeName : doc.uid,
    codeLabel: doc.data.code_label?.trim() || doc.uid.toUpperCase(),
    flagUrl: asImageSrc(doc.data.flag),
    direction: doc.data.direction === 'rtl' ? 'rtl' : 'ltr',
  };
}

/** The `language` UIDs listed in `site_settings.available_languages`, in order. */
export function availableLanguageCodes(siteSettings: Content.SiteSettingsDocument | null): Array<string> {
  if (!siteSettings) {
    return [];
  }

  return siteSettings.data.available_languages
    .map((item) => (isFilled.contentRelationship(item.language) ? item.language.uid : undefined))
    .filter((uid): uid is string => typeof uid === 'string');
}

/**
 * The picker list: enabled documents in `site_settings.available_languages`
 * order, then any other enabled document appended alphabetically by native
 * name, and English guaranteed first when nothing else provides it.
 */
export function orderLanguages(
  docs: Array<Content.LanguageDocument>,
  orderedCodes: Array<string>,
): Array<LanguageOption> {
  const enabled = new Map(docs.filter((doc) => doc.data.enabled === true).map((doc) => [doc.uid, doc]));

  const listed = orderedCodes
    .map((code) => enabled.get(code))
    .filter((doc): doc is Content.LanguageDocument => doc !== undefined);

  const listedCodes = new Set(listed.map((doc) => doc.uid));

  const unlisted = [...enabled.values()]
    .filter((doc) => !listedCodes.has(doc.uid))
    .map(toLanguageOption)
    .sort((a, b) => a.nativeName.localeCompare(b.nativeName));

  const options = [...listed.map(toLanguageOption), ...unlisted];

  if (options.some((option) => option.code === ENGLISH_CODE)) {
    return options;
  }

  // English is never absent from the picker. Prefer a disabled/unlisted `en`
  // document's own name and flag over the bundled fallback.
  const englishDoc = docs.find((doc) => doc.uid === ENGLISH_CODE);

  return [englishDoc ? toLanguageOption(englishDoc) : englishOption, ...options];
}
